import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { TabNavigation } from 'common/components';
import { ERoute } from 'common/const';
import { DEFAULT_LIST_OFFSET, LIST_LIMIT_0, LIST_LIMIT_20 } from 'common/config';
import { InventoryProvider } from 'common/context';
import { UserHooks } from 'common/hooks';
import { Package, Report } from 'common/svg';
import { showSuccessMessage } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  InventoryListTab,
  InventoryResponsibleAppointModal,
  InventoryTransferActModal,
  TransferInventoryActionListTab,
  WriteOffInventoryActionListTab,
} from 'entities/Inventory/components';
import { EInventoryListPageTab, EInventoryStatus } from 'entities/Inventory/Inventory.enum';
import { ICreateInventoryPayload, IInventoryPendingAssignee } from 'entities/Inventory/Inventory.models';
import { getInventoriesTabsCounts } from 'entities/Inventory/Inventory.helper';
import { inventoryTransport } from 'entities/Inventory/Inventory.transport';
import { SelectGoodCard } from 'entities/Goods/components';
import { IGood } from 'entities/Goods/Goods.models';
import { getGoodList } from 'entities/Goods/Good.slice';
import { createInventory, selectIsInventoryLoading } from 'entities/Inventory/Inventory.slice';
import { getUserList, selectCurrentUser, selectUserInventoryStats } from 'entities/User/User.slice';

export const InventoryListPage: FC = () => {
  const dispatch = useAppDispatch();
  // inventory
  const isInventoryLoading = useAppSelector(selectIsInventoryLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);
  const userInventoryStats = useAppSelector(selectUserInventoryStats);

  const [goods, setGoods] = useState<IGood | null>(null);
  const [openSelectGoodCard, setOpenSelectGoodCard] = useState<boolean>(false);
  const [openInventoryResponsibleAppointModal, setOpenInventoryResponsibleAppointModal] = useState<boolean>(false);
  const [openTransferActModal, setOpenTransferActModal] = useState<boolean>(false);
  const [pendingAssignees, setPendingAssignees] = useState<IInventoryPendingAssignee[]>([]);
  const [transferActLoading, setTransferActLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { tab } = useParams();
  const { isAccountAdmin, isHeadOfDepartment } = UserHooks.useCurrentUserRole();

  useEffect(() => {
    if (!tab) {
      navigate(`${ERoute.InventoryList}/${EInventoryListPageTab.Inventories}`, { replace: true });
    }
  }, [tab]);

  const isInventoriesTab = tab === EInventoryListPageTab.Inventories;
  const { totalInventoriesCount, totalTransferActionsCount, totalWriteOffActionsCount } = getInventoriesTabsCounts(
    currentUser,
    userInventoryStats,
  );

  const handleOpenSelectGoodCard = async (): Promise<void> => {
    await dispatch(
      getGoodList({
        limit: LIST_LIMIT_20,
        offset: DEFAULT_LIST_OFFSET,
      }),
    );
    await dispatch(getUserList({ limit: LIST_LIMIT_0 }));
    setOpenSelectGoodCard(true);
  };

  const closeSelectGoodCard = (): void => {
    setOpenSelectGoodCard(false);
  };

  const handleSelectGoods = (goods: IGood) => {
    setGoods(goods);
    setOpenInventoryResponsibleAppointModal(true);
  };

  const closeInventoryResponsibleAppointModal = () => {
    setOpenInventoryResponsibleAppointModal(false);
  };

  const appointInventoryResponsible = (value: ICreateInventoryPayload, onSuccess: () => void) => {
    if (!currentUser || !goods) {
      return;
    }

    dispatch(
      createInventory({
        goodId: goods.id,
        subdivisionId: value.subdivisionId,
        accountId: currentUser.accountId,
        count: value.count,
        userId: value.userId,
        status: EInventoryStatus.Available,
        onSuccess: () => {
          onSuccess();
          setOpenSelectGoodCard(false);
        },
      }),
    );
  };

  const handleOpenTransferActModal = async () => {
    try {
      setTransferActLoading(true);
      const response = await inventoryTransport.getPendingAssignees();
      setPendingAssignees(response.data);
      setOpenTransferActModal(true);
    } finally {
      setTransferActLoading(false);
    }
  };

  const closeTransferActModal = () => {
    setOpenTransferActModal(false);
  };

  const handleTransferActSubmit = async (assigneeId: number) => {
    try {
      setTransferActLoading(true);
      await inventoryTransport.sendInventoryDiff({ assigneeId });
      showSuccessMessage('Акт передачи отправлен.');
      setOpenTransferActModal(false);
    } finally {
      setTransferActLoading(false);
    }
  };

  const header = useMemo(() => {
    return (
      <>
        <div className="text-body color-dark-grey mb-6">Учёт инструмента</div>

        <div className="flex align-start justify-between">
          <TabNavigation
            items={[
              {
                label: (
                  <span className="ff-regular fs-xxl" style={{ lineHeight: 1.15 }}>
                    {isAccountAdmin || isHeadOfDepartment ? 'Все инструменты' : 'Мои инструменты'}
                    {!!totalInventoriesCount && (
                      <sup className="ml-2 ff-regular fs-l" style={{ top: -16 }}>
                        {totalInventoriesCount}
                      </sup>
                    )}
                  </span>
                ),
                path: `${ERoute.InventoryList}/${EInventoryListPageTab.Inventories}`,
              },
              {
                label: (
                  <span className="ff-regular fs-xxl" style={{ lineHeight: 1.15 }}>
                    {isAccountAdmin ? 'Запросы о передаче' : 'Передача инструмента'}
                    {!!totalTransferActionsCount && (
                      <sup className="ml-2 ff-regular fs-l" style={{ top: -16 }}>
                        {totalTransferActionsCount}
                      </sup>
                    )}
                  </span>
                ),
                path: `${ERoute.InventoryList}/${EInventoryListPageTab.TransferActions}`,
              },
              ...(isAccountAdmin || isHeadOfDepartment
                ? [
                    {
                      label: (
                        <span className="ff-regular fs-xxl" style={{ lineHeight: 1.15 }}>
                          Запросы о списании
                          {!!totalWriteOffActionsCount && (
                            <sup className="ml-2 ff-regular fs-l" style={{ top: -16 }}>
                              {totalWriteOffActionsCount}
                            </sup>
                          )}
                        </span>
                      ),
                      path: `${ERoute.InventoryList}/${EInventoryListPageTab.WriteOffActions}`,
                    },
                  ]
                : []),
            ]}
          />

          <div className="mt-3 flex align-center gap-4">
            {isInventoriesTab && isAccountAdmin && (
              <Button
                className="button-sm secondary"
                icon={<Package className="icon-package-dark-grey" />}
                onClick={handleOpenSelectGoodCard}
              >
                Добавить инструмент
              </Button>
            )}
            <Button
              className="button-sm secondary"
              icon={<Report className="icon-report-dark-grey" />}
              loading={transferActLoading}
              onClick={handleOpenTransferActModal}
            >
              Акт передачи
            </Button>
          </div>
        </div>
      </>
    );
  }, [tab, currentUser, transferActLoading, totalInventoriesCount, totalTransferActionsCount, totalWriteOffActionsCount]);

  const renderContent = () => {
    switch (tab) {
      case EInventoryListPageTab.TransferActions: {
        return <TransferInventoryActionListTab header={header} />;
      }
      case EInventoryListPageTab.WriteOffActions: {
        return <WriteOffInventoryActionListTab header={header} />;
      }
      default: {
        return <InventoryListTab header={header} />;
      }
    }
  };

  return (
    <>
      <InventoryProvider>
        <div className="redesign inventory-list relative flex direction-column">{renderContent()}</div>
      </InventoryProvider>

      <SelectGoodCard open={openSelectGoodCard} allowManualAdd onCancel={closeSelectGoodCard} onSelect={handleSelectGoods} />

      <InventoryResponsibleAppointModal
        open={openInventoryResponsibleAppointModal}
        loading={isInventoryLoading}
        onCancel={closeInventoryResponsibleAppointModal}
        onSubmit={appointInventoryResponsible}
      />

      <InventoryTransferActModal
        open={openTransferActModal}
        loading={transferActLoading}
        assignees={pendingAssignees}
        onClose={closeTransferActModal}
        onSubmit={handleTransferActSubmit}
      />
    </>
  );
};
